import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { jwtDecode } from 'jwt-decode';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { useLocation, Link, Routes, Route, useNavigate } from 'react-router-dom';
import { useAPICall, useAppDispatch } from '../../hooks';
import { Dashboard } from '../dashboard/[campaignId]';
import MemberProgressView from '../dashboard/member-progress/MemberProgressView';
import { DashboardAlt } from '../dashboard/join-team/[teamId]';
import {
  FAIcon,
  IconStyle,
  Page,
  Theme,
  AdminToast,
  ToastProvider,
  useBreakpoint,
  Button,
  ButtonWidth,
  ButtonIconPosition,
  ButtonStyles,
} from '../../ui/components';
import { MyTeam } from '../dashboard/my-team';
import { JoinTeam } from '../dashboard/join-team/register/[loginId]';
import { CreateTeam } from '../dashboard/create-team/list';
// import { Coach } from '../dashboard/campaigns/[campaignId]/coach';
import { LoggerService, getAccessToken, getAmwayLogoutUrl, getEnvironment } from '../../ui/utils';
import { AppTokenModel, DynamicObjectInterface } from '../../sdk/type';
//import CoachService from '../../sdk/services/coachService';
import MenuService from '../../sdk/services/menuService';
import AttachmentService from '../../sdk/services/attachmentService';
import userService from '../../sdk/services/userService';
import ParticipantService from '../../sdk/services/participantService';
import { TeamDetails } from '../dashboard/team-list/team-detail/[teamId]';
import { TeamList } from '../dashboard/team-list/list';
// import CoachDetails from '../dashboard/campaigns/[campaignId]/[coachEnrollmentId]';
import { TeamProfile } from '../dashboard/team-list/team-profile';
import { NoResultPage } from '../dashboard/error-page/no-result-found';
import { ParticipantList } from '../dashboard/participant-list/list';
import { ProfileDetails } from '../profile/Profile';
import { EditPersonalDetails } from '../profile/edit/personalDetail';
import personalizeImg from '../../assets/image/ProfileImageUpload.svg';
import { AddParticipantDashboard } from '../dashboard/team-list/team-detail/add-participant/[teamId]';
// import GroupPlus from '../../assets/image/icons/group-user-plus.svg';
//import { CoachEnrollment } from '../dashboard/coach-enrollment/list';
import { isTabletLandscape } from '../../ui/components/Breakpoint/BreakpointProvider';
import { NotificationDrawer } from '../../ui/app-components';
import { clearLogin } from '../../sdk/store/authActions';
import TabsComponent from '../../ui/components/TabComponent/TabsComponent';
import { TabType } from '../../ui/components/TabComponent/types/enum';
import Breadcrumb from '../../ui/components/Breadcrumb/Breadcrumb';
import { generateBreadcrumbs } from '../../ui/utils/breadcrumb';
import { REACT_APP_COUNTRY } from '../../envVars';
import { Homepage } from './Homepage';
import styles from './Home.module.scss';

interface Enrollment {
  enrollmentType: 'LEADER' | 'COACH' | 'PARTICIPANT';
  enrollmentId: number;
  teamId: string;
  coachId: string;
  participationType: string;
}

interface SubmenuVisibility {
  [key: string]: boolean;
}
interface MenuType {
  title: DynamicObjectInterface;
  url?: string;
  linkUrl?: string;
  icon?: IconName;
  iconStyle?: IconStyle;
  component?: JSX.Element;
  onClick?: () => void;
  submenus?: MenuType[];
  campaignId?: string;
  campaignName?: string;
  joinTeam?: boolean;
  teamId?: string;
  sequence?: number;
  isExternalUrl?: boolean;
  customStyle?: DynamicObjectInterface;
}

interface Tab {
  id: string;
  title: string;
  linkUrl: string;
  campaignTitle: string;
}

type GroupedTabs = { [key: string]: Tab[] };

export interface MenuDataType {
  campaignId: string;
  campaignName: string;
  coachId: string;
  isLeader: boolean;
  teamId: number;
  enrollment: Enrollment[];
  appointmentLink?: string;
  campaignTextLinkDisplay: boolean;
  teamList: [];
  coachList: [];
  configJson: DynamicObjectInterface;
  postAssessmentReminderDays: number;
}

export interface UserProfileData {
  name: string;
  email: string;
  phone: string;
  gender: string;
  race: string;
  occupation: string;
  incomeRange: string;
  address: string;
  state: string;

  loginId: string;
  hightestPinLevel: number;
  pinLevel: number;
  typeOfUser: string;
  uplineDD: string;
  ddName: string;
  uplineDI: string;
  diName: string;
  loaName: string;
}

export interface ParticipationDataType {
  recId: number;
}

export const Home = () => {
  const access_token = getAccessToken() || '';
  let decoded: AppTokenModel | null = access_token ? jwtDecode(access_token) : null;
  const isLocal = useRef(['LOCAL'].includes(getEnvironment())).current;

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const drawerRef: DynamicObjectInterface = useRef(null); // Ref for the drawer element
  const viewPortType = useBreakpoint();

  const [submenuVisibility, setSubmenuVisibility] = useState<SubmenuVisibility>({});
  const [isMobile, setIsMobile] = useState(false);
  const [mobileDrawer, setMobileDrawer] = useState(true);
  const [menuData, setMenuData] = useState<MenuDataType[]>([]);
  const [validateParticipationData, setValidateParticipationData] = useState<ParticipationDataType>();
  const [groupedTabList, setGroupedTabList] = useState<GroupedTabs>();
  const [campaignTabList, setCampaignTabList] = useState<Tab[]>([]);
  //const [isCoach, setIsCoach] = useState(false);
  const [isProfileComplete, setIsProfileComplete] = useState(false);
  const [incompletePercentage, setIncompletePercentage] = useState<DynamicObjectInterface>('50');
  const [selectedTab, setSelectedTab] = useState(0);
  const apiCall = useAPICall();

  const [breadcrumbData, setBreadcrumbData] = useState<{ name: string; link: string }[]>([]);

  useEffect(() => {
    let rootEl = document.querySelector('#root');
    if (rootEl) rootEl.scrollTo(0, 0);

    const pathName = window.location.pathname;
    if (!pathName.startsWith('/admin')) {
      const breadcrumbs = generateBreadcrumbs(pathName, t);
      setBreadcrumbData(breadcrumbs);
    }
  }, [window.location.pathname]);

  const fetchMenuDetails = async () => {
    try {
      let menuDataResult = await apiCall(MenuService?.getMenu);
      setMenuData(menuDataResult?.data?.data || []);

      let validateParticipationResult = await apiCall(ParticipantService?.validateJoinTeam, {
        loginId: decoded?.loginId,
        partyId: decoded?.partyId,
      });

      const validateParticipation = validateParticipationResult?.data?.data;

      setValidateParticipationData(validateParticipation);
    } catch (error) {
      LoggerService.log('Failed to retrieve audit history log', error);
    }
  };

  useEffect(() => {
    fetchMenuDetails();
  }, []);

  useEffect(() => {
    const isMobile_ = ['xs', 'sm', 'md'].includes(viewPortType);

    if (isMobile_ || isTabletLandscape()) {
      setMobileDrawer(false);
    } else {
      setMobileDrawer(true);
    }
    setIsMobile(isMobile_ || isTabletLandscape());
  }, [viewPortType, isTabletLandscape()]);

  // useEffect(() => {
  //   (async () => {
  //     if (decoded?.loginId) {
  //       let res = await apiCall(CoachService?.getCoachByAboId, decoded?.loginId);
  //       setIsCoach(!!res?.data?.data?.coachID);
  //     }
  //   })();
  // }, [decoded?.loginId]);

  const toggleSubmenu = (url: string, hasSubmenu: boolean) => {
    if (hasSubmenu) {
      setSubmenuVisibility(prevState => ({
        ...prevState,
        [url]: !prevState[url],
      }));
    } else {
      navigate(`/${url}`);
    }
  };

  const isMenuActive = (menu: MenuType) => {
    if (location.pathname === '/') {
      return menu.url === '/';
    }
    if (menu.submenus) {
      let paths = location.pathname.split('/').filter(path => path && path !== 'd');
      let _paths = menu.linkUrl?.split('/');
      return (
        JSON.stringify(paths) === JSON.stringify(_paths) ||
        menu.submenus.some((submenu: MenuType) => location.pathname.includes('/' + submenu.linkUrl))
      );
    }
    return location.pathname === '/' + menu.url;
  };

  const [profileImageUrl, setProfileImageUrl] = useState<string | undefined>('');

  const fetchProfileImage = async () => {
    try {
      const response = await AttachmentService.downloadProfileImage(decoded?.userId ?? '');
      if (response.data && response.data.data && response.data.data.image) {
        setProfileImageUrl(response.data.data.image);
      }
    } catch (error) {
      LoggerService.log('Error downloading image', error);
    }
  };

  useEffect(() => {
    fetchProfileImage();
  }, []);

  const notificationComponent = useMemo(() => {
    if (!['xs', 'sm', 'md'].includes(viewPortType)) {
      return <NotificationDrawer />;
    } else {
      return null;
    }
  }, [viewPortType]);

  const campaignLinks: MenuType[] = useMemo(() => {
    return menuData.map((campaign: MenuDataType, index) => {
      let _submenus: MenuType[] = [];
      const items = {
        progress: {
          title: t('dashboard.sidebar.label.myProgress'),
          url: `campaigns/${campaign.campaignId}`,
          linkUrl: `campaigns/${encodeURIComponent(campaign.campaignId)}`,
          component: <Dashboard decodedData={decoded} index={index} campaignData={menuData} />,
          sequence: 1,
        },
        myTeam: {
          title: t('dashboard.sidebar.label.myTeam'),
          url: `campaigns/${campaign.campaignId}/myTeam`,
          linkUrl: `campaigns/${encodeURIComponent(campaign.campaignId)}/myTeam`,
          component: <MyTeam decodedData={decoded} index={index} campaignData={menuData} />,
          sequence: 2,
        },
        participants: {
          title: t('dashboard.sidebar.label.participants'),
          url: `campaigns/${campaign.campaignId}/participants`,
          linkUrl: `campaigns/${encodeURIComponent(campaign.campaignId)}/participants`,
          component: (
            <ParticipantList
              index={index}
              decodedData={{ ...decoded, campaignId: campaign.campaignId, campaignName: campaign.campaignName }}
              campaignData={menuData}
            />
          ),
          sequence: 3,
        },
        teams: {
          title: t('dashboard.sidebar.label.teams'),
          url: `campaigns/${campaign.campaignId}/teams`,
          linkUrl: `campaigns/${encodeURIComponent(campaign.campaignId)}/teams`,
          component: (
            <TeamList
              index={index}
              decodedData={{ ...decoded, campaignId: campaign.campaignId, campaignName: campaign.campaignName }}
              campaignData={menuData}
            />
          ),
          sequence: 4,
        },
        // coach: {
        //   title: t('dashboard.sidebar.label.coaches'),
        //   url: `campaigns/${campaign.campaignId}/coach`,
        //   linkUrl: `campaigns/${encodeURIComponent(campaign.campaignId)}/coach`,
        //   component: <Coach decodedData={decoded} />,
        //   sequence: 5,
        // },
      };
      campaign.enrollment.forEach(e => {
        switch (e.enrollmentType) {
          // case 'LEADER':
          //   _submenus.push(items.coach as MenuType);
          //   _submenus.push(items.teams as MenuType);
          //   _submenus.push(items.progress as MenuType);
          //   _submenus.push(items.participants as MenuType);
          //   break;
          case 'COACH':
            _submenus.push(items.progress as MenuType);
            if (e.participationType !== 'ind') _submenus.push(items.myTeam as MenuType);
            // _submenus.push(items.teams as MenuType);
            // _submenus.push(items.participants as MenuType);
            break;
          case 'PARTICIPANT':
            _submenus.push(items.progress as MenuType);
            if (e.participationType !== 'ind') _submenus.push(items.myTeam as MenuType);
            break;
          default:
            break;
        }
      });

      const ids = _submenus.map(s => s.title);
      _submenus = _submenus.filter((s, index) => !ids.includes(s.title, index + 1));

      return {
        title: campaign.campaignName,
        url: `campaigns/${campaign.campaignId}`,
        linkUrl: `campaigns/${encodeURIComponent(campaign.campaignId)}`,
        component: <Dashboard decodedData={decoded} index={index} campaignData={menuData} />,
        //submenus: _submenus.toSorted((a: MenuType, b: MenuType) => (a.sequence ?? 0) - (b.sequence ?? 0)),
        submenus: _submenus.sort((a: MenuType, b: MenuType) => (a.sequence ?? 0) - (b.sequence ?? 0)),
      };
    });
  }, [menuData]);

  let links: MenuType[] = useMemo(() => {
    let _links: MenuType[] = [
      {
        title: t('dashboard.sidebar.label.home'),
        url: '/',
        customStyle: { padding: '12px 12px 12px 36px' },
        component: <Homepage index={0} decodedData={{ ...decoded }} campaignData={menuData} />,
      },
      {
        title: notificationComponent,
        customStyle: {},
        url: '',
      },
    ];

    _links = _links.concat(campaignLinks);

    //--set tabs link under campaign menu when mobile view --//
    if (campaignLinks.length > 0) {
      const tabList = campaignLinks.flatMap(
        campaignLink =>
          campaignLink.submenus?.map((link, index) => ({
            id: `tab${index + 1}`,
            title: link.title,
            linkUrl: link.url ?? '',
            campaignTitle: campaignLink.title,
          })) || [],
      );

      const groupedTabList = tabList.reduce<GroupedTabs>((acc, tab) => {
        if (!acc[tab.campaignTitle]) {
          acc[tab.campaignTitle] = [];
        }
        acc[tab.campaignTitle].push(tab);
        return acc;
      }, {});

      setGroupedTabList(groupedTabList);
    }

    return [..._links];
  }, [menuData, campaignLinks, viewPortType]);

  useEffect(() => {
    const url = window.location.pathname;

    if (url.includes('/campaigns/')) {
      if (groupedTabList) {
        const campaignTitles = Object.keys(groupedTabList);
        if (campaignTitles.length > 0) {
          for (const campaignTitle of campaignTitles) {
            const tabs = groupedTabList[campaignTitle];
            const tabIndex = tabs.findIndex(tab => tab.linkUrl === url.slice(1));
            if (tabIndex !== -1) {
              // If a matching tab is found
              setCampaignTabList(tabs);
              setSelectedTab(tabIndex); // Set the index of the matching tab
              break; // Stop after finding the match
            } else {
              setCampaignTabList([]);
            }
          }
        }
      }
    } else {
      setCampaignTabList([]);
    }
  }, [location, groupedTabList]);

  useEffect(() => {
    const initialVisibility: SubmenuVisibility = {};
    links.slice(2).forEach(link => {
      if (link.submenus && link.submenus.length > 0) {
        initialVisibility[link.url ?? ''] = true; // Set to true to expand by default
      }
    });
    setSubmenuVisibility(initialVisibility);
  }, [links]);

  // const handleMenuClicked = (title?: DynamicObjectInterface) => {
  //   if (isMobile && title?.type?.name !== 'NotificationDrawer') {
  //     setMobileDrawer(false);
  //   }
  // };

  const incompletePercentageRange = (data: DynamicObjectInterface) => {
    let hasCompleted = 0;
    let totalMustComplete = REACT_APP_COUNTRY === 'MY' ? 6 : 3;

    if (data.phone) {
      hasCompleted += 1;
    }
    if (data.gender) {
      hasCompleted += 1;
    }
    if (data.race && REACT_APP_COUNTRY === 'MY') {
      hasCompleted += 1;
    }
    if (data.address) {
      hasCompleted += 1;
    }
    if (data.occupation && REACT_APP_COUNTRY === 'MY') {
      hasCompleted += 1;
    }
    if (data.incomeRange && REACT_APP_COUNTRY === 'MY') {
      hasCompleted += 1;
    }

    const calculatedData = Math.floor((hasCompleted / totalMustComplete) * 100);
    return calculatedData;
  };

  useEffect(() => {
    const profileCompletion = async () => {
      if (decoded?.userId) {
        try {
          const res = await apiCall(userService.getUserDetails, decoded?.userId?.toString() ?? '');
          if (res.data.data) {
            let _data: UserProfileData = res.data.data;
            const randomPercentage = await incompletePercentageRange(_data); // Generates a random number between 0 and 100
            if (randomPercentage === 100) {
              setIncompletePercentage(randomPercentage);
              setIsProfileComplete(true);
            } else {
              setIncompletePercentage(randomPercentage);
              setIsProfileComplete(false);
            }
          }
        } catch (error) {
          LoggerService.log('Error fetching user details:', error);
        }
      }
    };

    profileCompletion();
  }, [decoded]);

  const handleNewTeam = () => {
    if (isMobile) setMobileDrawer(false);
    if (validateParticipationData?.recId) {
      navigate(`/join-team/register?tmI=ind&cmI=${validateParticipationData?.recId}`);
    } else {
      navigate('/create-team');
    }
  };

  // const handleCoachEnrollment = () => {
  //   if (isMobile) setMobileDrawer(false);
  //   navigate('/coachEnrollment');
  // };

  const logoutFn = (): Function => {
    const localSignIn = () => {
      setTimeout(() => {
        window.location.href = '/';
      }, 0);
    };

    const amwayIdSignOut = () => {
      LoggerService.log('AmwayId signing out');
      window.location.href = getAmwayLogoutUrl();
    };

    if (isLocal) return localSignIn;

    return amwayIdSignOut;
  };

  const doLogout = () => {
    localStorage.removeItem('pinNumber');
    localStorage.removeItem('appToken');
    localStorage.removeItem('exp');
    localStorage.removeItem('userId');
    localStorage.removeItem('loginId');
    window.location.href = '/';
    dispatch(clearLogin(logoutFn()));
  };

  const handleTabClick = (tabId: string, tabIndex: number) => {
    navigate(campaignTabList[tabIndex].linkUrl);
    setSelectedTab(tabIndex);
    LoggerService.log(`Tab clicked: ${tabId} at index ${tabIndex}`);
  };

  return (
    <Page className={`${styles['main']} ${styles['main-container']}`}>
      {isMobile ? (
        <div>
          <div
            className={styles['main-hamburger-icon']}
            onClick={() => {
              setMobileDrawer(!mobileDrawer);
            }}
          >
            <FAIcon iconName="bars" iconStyle={IconStyle.SOLID} className={`${styles['icon-tester']} fa-lg`} />
          </div>
          {campaignTabList.length > 0 ? (
            <div className={styles['main-tab']}>
              <TabsComponent
                theme={Theme.PURPLE}
                tabList={campaignTabList}
                onTabClick={handleTabClick}
                sortAndFilter={false}
                selectedTab={selectedTab}
                tabType={TabType.COMPONENT}
              />
            </div>
          ) : (
            <div className={`${styles['main-tab']} ${styles['main-tab-breadcrumb-container']}`}>
              <div className={styles['main-tab-breadcrumb-item']}>
                <Breadcrumb breadcrumbData={breadcrumbData} maxPage={2} />
              </div>
            </div>
          )}
        </div>
      ) : null}
      {mobileDrawer ? (
        <>
          <aside className={`${styles['main-sidebar']} ${styles['main-card']}`} ref={drawerRef}>
            <div className={`${styles['main-user-img']}`}>
              <div className={styles['img-container']}>
                <img
                  src={profileImageUrl ? profileImageUrl : personalizeImg}
                  alt="personalize"
                  onClick={() => {
                    navigate('/profile?id=' + decoded?.userId);
                    if (isMobile) setMobileDrawer(false);
                  }}
                  className={styles['main-profile-image']}
                />
              </div>
              <div>{decoded?.name}</div>
              <nav>
                {isProfileComplete ? (
                  <Link
                    to={'/profile?id=' + decoded?.userId}
                    onClick={() => {
                      if (isMobile) setMobileDrawer(false);
                    }}
                    className={styles['view-profile-link']}
                  >
                    {t('dashboard.sidebar.label.viewProfile')}
                  </Link>
                ) : (
                  <div>
                    <div className={styles['incomplete-Profile']}>
                      {t('dashboard.sidebar.label.incompleteProfilePercent').replace(
                        '{incomplete-profile-percent}',
                        incompletePercentage,
                      )}
                    </div>
                    <Link
                      to={'/profile?id=' + decoded?.userId}
                      onClick={() => {
                        if (isMobile) setMobileDrawer(false);
                      }}
                    >
                      {t('dashboard.sidebar.label.clickToComplete')}
                    </Link>
                  </div>
                )}
              </nav>
              <hr />
            </div>
            <div className={styles['new-team-button-container']}>
              <Button
                // customIcon={true}
                // customIconComponent={<img src={GroupPlus} alt="bg" height={'20px'} />}
                title={t('dashboard.sidebar.button.joinNow')}
                className={styles['side-bar-button']}
                onClick={handleNewTeam}
                theme={Theme.GREEN}
                // iconPosition={ButtonIconPosition.LEFT}
                buttonWidth={ButtonWidth.FULL_WIDTH}
              />
            </div>
            {/* {isCoach ?? (
              <div className={styles['coach-enrollment-button-container']}>
                <Button
                  className={styles['side-bar-button']}
                  title={'Coach Enrollment'}
                  onClick={handleCoachEnrollment}
                  theme={Theme.GREEN}
                  iconPosition={ButtonIconPosition.LEFT}
                  buttonWidth={ButtonWidth.FULL_WIDTH}
                />
              </div>
            )} */}
            <nav className={`${styles['main-nav']}`}>
              <div className={styles['main-nav-title-top']}>{t('dashboard.sidebar.menu.mainMenu').toUpperCase()}</div>
              <ol className={styles['nav-list']}>
                {links.slice(0, 2).map((link, index) => {
                  const isActive = isMenuActive(link);
                  const hasSubmenu = link.submenus && link.submenus.length > 0;
                  return (
                    <li
                      key={index}
                      className={`${styles['main-nav-item']} ${isActive ? styles['main-nav-item-active'] : ''}`}
                    >
                      {hasSubmenu ? (
                        <div>
                          <Link
                            to={link.linkUrl || ''}
                            className={`${styles['main-nav-item-link']} ${
                              isActive ? styles['main-nav-item-link-active'] : ''
                            }`}
                            // onClick={() => handleMenuClicked(link.title)}
                            onClick={() => {
                              if (isMobile) setMobileDrawer(false);
                            }}
                          >
                            <div className={styles['parent-menu']}>
                              {link.title}
                              <span
                                className={styles['chevron-icon-container']}
                                onClick={e => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  toggleSubmenu(link.url ?? '', hasSubmenu);
                                }}
                              >
                                <FAIcon
                                  iconName={submenuVisibility[link.url ?? ''] ? 'chevron-up' : 'chevron-down'}
                                  iconStyle={IconStyle.SOLID}
                                />
                              </span>
                            </div>
                          </Link>
                          {submenuVisibility[link.url ?? ''] && (
                            <ul>
                              {link?.submenus?.map((submenu, submenuIndex) => {
                                let isValidExternalLink = submenu.url?.startsWith('http');
                                return (
                                  <li key={submenuIndex} className={`${styles['main-nav-subitem']}`}>
                                    <Link
                                      to={
                                        submenu.isExternalUrl
                                          ? isValidExternalLink
                                            ? (submenu.url ?? '')
                                            : location.pathname
                                          : submenu.linkUrl || ''
                                      }
                                      className={`${styles['main-nav-subitem-sub-link']} ${
                                        location.pathname === `${'/' + submenu.linkUrl}`
                                          ? styles['main-nav-subitem-sub-link-active']
                                          : ''
                                      }`}
                                      // onClick={() => handleMenuClicked(submenu.title)}
                                      onClick={() => {
                                        if (isMobile) setMobileDrawer(false);
                                      }}
                                      target={isValidExternalLink ? '_blank' : '_self'}
                                    >
                                      <div className={styles['main-nav-subitem-sub-link-container']}>
                                        <div className={styles['main-nav-subitem-sub-link-container-text']}>
                                          {submenu.title}
                                        </div>
                                      </div>
                                    </Link>
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                        </div>
                      ) : (
                        <>
                          {link.url === '' ? (
                            <div
                              // onClick={() => handleMenuClicked(link.title)}
                              onClick={() => {
                                if (isMobile) setMobileDrawer(false);
                              }}
                              className={`${styles['main-nav-item-link']} ${
                                isActive ? styles['main-nav-item-link-active'] : ''
                              }`}
                            >
                              <div className={styles['main-nav-title']} style={link.customStyle}>
                                {link.title}
                              </div>
                            </div>
                          ) : (
                            <Link
                              to={link.url ?? ''}
                              className={`${styles['main-nav-item-link']} ${
                                isActive ? styles['main-nav-item-link-active'] : ''
                              }`}
                              // onClick={() => handleMenuClicked(link.title)}
                              onClick={() => {
                                if (isMobile) setMobileDrawer(false);
                              }}
                            >
                              <div className={styles['main-nav-title']} style={link.customStyle}>
                                {link.title}
                              </div>
                            </Link>
                          )}
                        </>
                      )}
                    </li>
                  );
                })}
              </ol>
              <div className={styles['menu-divider']} />
              {links.slice(2).length !== 0 ? (
                <div className={`${styles['main-nav-title-top']} ${styles['program']}`}>
                  {t('dashboard.sidebar.menu.program').toUpperCase()}
                </div>
              ) : null}
              <ol className={styles['nav-list']}>
                {links.slice(2).map((link, index) => {
                  const isActive = isMenuActive(link);
                  const hasSubmenu = link.submenus && link.submenus.length > 0;
                  return (
                    <li
                      key={index}
                      className={`${styles['main-nav-item']} ${isActive ? styles['main-nav-item-active'] : ''}`}
                    >
                      {hasSubmenu ? (
                        <div>
                          <Link
                            to={link.linkUrl || ''}
                            className={`${styles['main-nav-item-link']} ${
                              isActive ? styles['main-nav-item-link-active'] : ''
                            }`}
                            // onClick={() => handleMenuClicked(link.title)}
                            onClick={() => {
                              if (isMobile) setMobileDrawer(false);
                            }}
                          >
                            <div
                              className={styles['parent-menu']}
                              onClick={e => {
                                e.preventDefault();
                                e.stopPropagation();
                                toggleSubmenu(link.url ?? '', hasSubmenu);
                              }}
                            >
                              {link.title}
                              <span
                                className={styles['chevron-icon-container']}
                                onClick={e => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  toggleSubmenu(link.url ?? '', hasSubmenu);
                                }}
                              >
                                <FAIcon
                                  iconName={submenuVisibility[link.url ?? ''] ? 'chevron-up' : 'chevron-down'}
                                  iconStyle={IconStyle.SOLID}
                                />
                              </span>
                            </div>
                          </Link>
                          {submenuVisibility[link.url ?? ''] && (
                            <ul>
                              {link?.submenus?.map((submenu, submenuIndex) => {
                                let isValidExternalLink = submenu.url?.startsWith('http');
                                return (
                                  <li key={submenuIndex} className={`${styles['main-nav-subitem']}`}>
                                    <Link
                                      to={
                                        submenu.isExternalUrl
                                          ? isValidExternalLink
                                            ? (submenu.url ?? '')
                                            : location.pathname
                                          : submenu.linkUrl || ''
                                      }
                                      className={`${styles['main-nav-subitem-sub-link']} ${
                                        location.pathname === `${'/' + submenu.linkUrl}`
                                          ? styles['main-nav-subitem-sub-link-active']
                                          : ''
                                      }`}
                                      // onClick={() => handleMenuClicked(submenu.title)}
                                      onClick={() => {
                                        if (isMobile) setMobileDrawer(false);
                                      }}
                                      target={isValidExternalLink ? '_blank' : '_self'}
                                    >
                                      <div className={styles['main-nav-subitem-sub-link-container']}>
                                        <div className={styles['main-nav-subitem-sub-link-container-text']}>
                                          {submenu.title}
                                        </div>
                                      </div>
                                    </Link>
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                        </div>
                      ) : (
                        <Link
                          to={link.url ?? ''}
                          className={`${styles['main-nav-item-link']} ${
                            isActive ? styles['main-nav-item-link-active'] : ''
                          }`}
                          // onClick={() => handleMenuClicked(link.title)}
                          onClick={() => {
                            if (isMobile) setMobileDrawer(false);
                          }}
                        >
                          <div className={styles['main-nav-title']} style={link.customStyle}>
                            {link.title}
                          </div>
                        </Link>
                      )}
                    </li>
                  );
                })}
              </ol>
            </nav>
            <div className={styles['new-team-button-container']}>
              <Button
                icon={{ iconName: 'sign-out', iconStyle: IconStyle.SOLID }}
                title={t('dashboard.sidebar.button.logout')}
                className={styles['side-bar-button']}
                onClick={doLogout}
                theme={Theme.ERROR}
                buttonStyle={ButtonStyles.OUTLINE}
                iconPosition={ButtonIconPosition.LEFT}
                buttonWidth={ButtonWidth.FULL_WIDTH}
              />
            </div>
          </aside>
        </>
      ) : null}
      <ToastProvider>
        <AdminToast timeout={100000} topPosition="100px">
          <main className={`${styles['main-content']}`}>
            <Routes>
              {links.map((link, key) => (
                <Route key={key} path={link.url} element={link.component} />
              ))}
              {links.flatMap(link =>
                link.submenus
                  ? link.submenus.map(submenu => (
                      <Route key={submenu.url} path={submenu.url} element={submenu.component} />
                    ))
                  : [],
              )}
              <Route
                path="/"
                element={
                  <Homepage
                    index={0}
                    decodedData={{
                      ...decoded,
                    }}
                    campaignData={menuData}
                  />
                }
              />
              <Route path="join-team" element={<DashboardAlt decodedData={decoded} />} />
              <Route path="create-team" element={<CreateTeam decodedData={decoded} />} />
              {/* <Route path="coachEnrollment" element={<CoachEnrollment decodedData={decoded} />} /> */}
              <Route path="join-team/register" element={<JoinTeam decodedData={decoded} />} />
              <Route path="error-page/no-result-found" element={<NoResultPage />} />
              <Route path="profile" element={<ProfileDetails decodedData={1} />} />
              <Route path="profile/edit" element={<EditPersonalDetails decodedData={1} />} />
              <Route path="campaigns/teams/add-participant" element={<AddParticipantDashboard />} />
              {menuData.map((campaign, idx1) => (
                <>
                  {campaign.teamList.map((enroll, idx2) => (
                    <>
                      <Route
                        key={`team_${idx1}_${idx2}`}
                        path={`campaigns/${campaign.campaignId}/teams/${enroll}`}
                        element={
                          <TeamDetails
                            dataCampaign={campaign}
                            decodedData={decoded}
                            index={idx1}
                            role={
                              campaign.enrollment.some((i: DynamicObjectInterface) => i.enrollmentType === 'COACH')
                                ? 'COACH'
                                : 'PARTICIPANT'
                            }
                          />
                        }
                      />
                      {[
                        `campaigns/${campaign.campaignId}/teams/${enroll}/user`,
                        `campaigns/${campaign.campaignId}/participants/${enroll}/user`,
                      ].map((path, index) => (
                        <Route
                          key={`user_${idx1}_${idx2}_${index}`}
                          path={path}
                          element={
                            <TeamProfile
                              decodedData={decoded}
                              role={
                                campaign.enrollment.some((i: DynamicObjectInterface) => i.enrollmentType === 'COACH')
                                  ? 'COACH'
                                  : campaign.enrollment.some(
                                        (i: DynamicObjectInterface) => i.enrollmentType === 'LEADER',
                                      )
                                    ? 'LEADER'
                                    : 'PARTICIPANT'
                              }
                            />
                          }
                        />
                      ))}
                      {/* My Team (MULTI) member view reuses the My Progress dashboard read-only. */}
                      <Route
                        key={`myteam_user_${idx1}_${idx2}`}
                        path={`campaigns/${campaign.campaignId}/myTeam/user`}
                        element={<MemberProgressView decodedData={decoded} index={idx1} campaignData={menuData} />}
                      />
                    </>
                  ))}

                  {/* {campaign.coachList.map((enroll, idx2) => (
                    <>
                      <Route
                        key={`coach_${idx1}_${idx2}`}
                        path={`campaigns/${campaign.campaignId}/coach/${enroll}`}
                        element={
                          <CoachDetails
                            dataCampaign={menuData}
                            decodedData={decoded}
                            index={idx1}
                            coachEnrollmentId={enroll}
                          />
                        }
                      />
                    </>
                  ))} */}
                </>
              ))}
              <Route path="*" element={<NoResultPage />} />
            </Routes>
          </main>
          {/* {uploadImageModal} */}
        </AdminToast>
      </ToastProvider>
    </Page>
  );
};
